import apiClient from './index';

export type Gateway = {
  id: string;
  name?: string | null;
  gateway_type?: string | null;
  lorawan_gateway_id?: string | null;
  lorawan_gateway_name?: string | null;
  mac_address?: string | null;
  host?: string | null;
  mqtt_topic?: string | null;
  monitoring_mode?: string | null;
  heartbeat_interval_sec?: number | null;
  status?: string | null;
  last_seen_at?: string | null;
  last_ping_at?: string | null;
  last_mqtt_at?: string | null;
  last_latency_ms?: number | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  installation_section?: string | null;
  installation_floor?: string | null;
  installation_place?: string | null;
  antenna_type?: string | null;
  antenna_height_m?: number | null;
  antenna_gain_dbi?: number | null;
  location_change_reason?: string | null;
  responsible_name?: string | null;
  responsible_phone?: string | null;
  responsible_email?: string | null;
  credentials?: string | null;
  notes?: string | null;
  residential_complex_id?: string | null;
  building_id?: string | null;
  corp_id?: string | null;
  created_at?: string | null;
  lorawan_linked?: boolean;
  lorawan_link_status?: string | null;
  lorawan_link_status_label?: string | null;
  lorawan_match_source?: string | null;
  lorawan_suggested_gateway_id?: string | null;
  lorawan_suggested_gateway_name?: string | null;
  lorawan_health_status?: string | null;
  lorawan_last_seen_at?: string | null;
  lorawan_uplinks_count?: number | null;
  lorawan_unique_devices_count?: number | null;
  lorawan_downlink_count?: number | null;
  lorawan_ack_count?: number | null;
  lorawan_rssi_avg?: number | null;
  lorawan_snr_avg?: number | null;
  lorawan_reasons?: string[];
  path_issue_focus?: string | null;
  path_issue_label?: string | null;
  path_issue_reason?: string | null;
  devices_total?: number;
  devices_ok?: number;
  devices_problem?: number;
};

export type GatewayPathDevice = {
  id: string;
  serial_number?: string | null;
  system_type?: string | null;
  status?: string | null;
  last_data_time?: string | null;
  apartment_id?: string | null;
  apartment_number?: string | null;
  is_problem: boolean;
};

export type GatewayPathHealth = {
  gateway_id: string;
  gateway_status?: string | null;
  path_issue_focus: string;
  path_issue_label: string;
  path_issue_reason?: string;
  devices_total: number;
  devices_ok: number;
  devices_problem: number;
  devices_warn: number;
  devices_error: number;
  devices_new: number;
  devices: GatewayPathDevice[];
};

export type GatewayLoRaWanRemote = {
  gateway_id: string;
  name?: string | null;
  description?: string | null;
  last_seen_at?: string | null;
  state?: string | null;
};

export type GatewayLoRaWanBuildingSummary = {
  building_id: string;
  building_name?: string | null;
  devices_total: number;
  devices_warning: number;
  devices_no_data: number;
  devices_one_gateway: number;
  gateway_count_avg?: number | null;
  rssi_avg?: number | null;
  snr_avg?: number | null;
  base_stations_total: number;
  base_stations_linked: number;
  base_stations_warning: number;
};

export type GatewayLoRaWanCoverageLink = {
  gateway_id: string;
  lorawan_gateway_id: string;
  building_id: string;
  devices_heard: number;
  building_devices_total: number;
  packets_count: number;
  rssi_avg?: number | null;
  snr_avg?: number | null;
  last_seen_at: string;
  gateway_latitude?: number | null;
  gateway_longitude?: number | null;
};

export type GatewayLoRaWanCoverage = {
  period_start: string;
  period_end: string;
  buildings_with_devices: number;
  buildings_covered: number;
  gateways_active: number;
  links: GatewayLoRaWanCoverageLink[];
};

export type GatewayLoRaWanFloorGateway = {
  gateway_id: string;
  gateway_name?: string | null;
  devices_heard: number;
  packets_count: number;
  rssi_avg?: number | null;
  rssi_min?: number | null;
  snr_avg?: number | null;
  snr_min?: number | null;
};

export type GatewayLoRaWanSignalDistribution = {
  p10?: number | null;
  p50?: number | null;
  p90?: number | null;
};

export type GatewayLoRaWanFloorCoverageItem = {
  corp_id: string;
  corp_name?: string | null;
  section_number?: string | null;
  floor_number?: string | null;
  devices_total: number;
  devices_active: number;
  coverage_percent: number;
  packets_count: number;
  gateways_count: number;
  rssi_avg?: number | null;
  rssi_min?: number | null;
  snr_avg?: number | null;
  snr_min?: number | null;
  rssi_distribution: GatewayLoRaWanSignalDistribution;
  snr_distribution: GatewayLoRaWanSignalDistribution;
  status: 'good' | 'warning' | 'critical';
  issue_codes: string[];
  gateways: GatewayLoRaWanFloorGateway[];
};

export type GatewayLoRaWanFloorGatewayRange = {
  gateway_id: string;
  gateway_name?: string | null;
  floor_from?: number | null;
  floor_to?: number | null;
  floors_count: number;
  devices_heard: number;
  packets_count: number;
  rssi_avg?: number | null;
  snr_avg?: number | null;
  installation_section?: string | null;
  installation_floor?: string | null;
  installation_place?: string | null;
  antenna_type?: string | null;
  antenna_height_m?: number | null;
  antenna_gain_dbi?: number | null;
  placement_changes_count: number;
  unique_devices_count: number;
  rssi_distribution: GatewayLoRaWanSignalDistribution;
  snr_distribution: GatewayLoRaWanSignalDistribution;
  downlink_count: number;
  ack_count: number;
  ack_rate?: number | null;
  gateway_choice_problem_count: number;
  move_comparison?: GatewayLoRaWanMoveComparison | null;
};

export type GatewayLoRaWanMoveComparison = {
  changed_at: string;
  window_days: number;
  before_packets: number;
  after_packets: number;
  before_devices: number;
  after_devices: number;
  before_rssi_p10?: number | null;
  after_rssi_p10?: number | null;
  before_snr_p10?: number | null;
  after_snr_p10?: number | null;
  verdict: 'improved' | 'worse' | 'unchanged' | 'insufficient_data';
};

export type GatewayLoRaWanPeriodMetrics = {
  devices_active: number;
  packets_count: number;
  coverage_percent: number;
  gateways_count: number;
  fcnt_missing: number;
  fcnt_duplicates: number;
  downlink_count: number;
  ack_count: number;
  ack_rate?: number | null;
  rssi_distribution: GatewayLoRaWanSignalDistribution;
  snr_distribution: GatewayLoRaWanSignalDistribution;
};

export type GatewayLoRaWanPeriodComparison = {
  previous: GatewayLoRaWanPeriodMetrics;
  coverage_delta: number;
  packets_delta_percent?: number | null;
  rssi_p10_delta?: number | null;
  snr_p10_delta?: number | null;
};

export type GatewayLoRaWanTimeProfile = {
  hour: number;
  packets_count: number;
  devices_active: number;
  active_days: number;
};

export type GatewayLoRaWanDailyPoint = {
  day: string;
  packets_count: number;
  devices_active: number;
  coverage_percent: number;
  rssi_p10?: number | null;
  snr_p10?: number | null;
};

export type GatewayLocationHistory = {
  id: string;
  gateway_id: string;
  residential_complex_id?: string | null;
  building_id?: string | null;
  corp_id?: string | null;
  location?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  installation_section?: string | null;
  installation_floor?: string | null;
  installation_place?: string | null;
  antenna_type?: string | null;
  antenna_height_m?: number | null;
  antenna_gain_dbi?: number | null;
  valid_from: string;
  valid_to?: string | null;
  change_reason?: string | null;
  changed_fields: string[];
  changed_by_user_id?: string | null;
  changed_by_name?: string | null;
  created_at: string;
};

export type GatewayLoRaWanFloorRecommendation = {
  code: string;
  severity: 'warning' | 'critical';
  corp_id: string;
  corp_name?: string | null;
  section_number?: string | null;
  floor_from?: number | null;
  floor_to?: number | null;
  suggested_floor?: number | null;
  devices_affected: number;
  action: 'check_floor' | 'review_gateway_position' | 'add_gateway';
};

export type GatewayLoRaWanFloorCoverage = {
  building_id: string;
  building_name?: string | null;
  period_start: string;
  period_end: string;
  devices_total: number;
  devices_active: number;
  floors_total: number;
  floors_problem: number;
  floors: GatewayLoRaWanFloorCoverageItem[];
  gateway_ranges: GatewayLoRaWanFloorGatewayRange[];
  recommendations: GatewayLoRaWanFloorRecommendation[];
  current: GatewayLoRaWanPeriodMetrics;
  comparison?: GatewayLoRaWanPeriodComparison | null;
  time_profile: GatewayLoRaWanTimeProfile[];
  daily_profile: GatewayLoRaWanDailyPoint[];
  confidence_percent: number;
};

export type GatewayLoRaWanHealthPoint = {
  period_start: string;
  period_end: string;
  health_status?: string | null;
  uplinks_count: number;
  unique_devices_count: number;
  downlink_count: number;
  ack_count: number;
  ack_rate?: number | null;
  rssi_avg?: number | null;
  snr_avg?: number | null;
  last_seen_at?: string | null;
  reasons?: string[];
};

export type GatewaySla = {
  gateway_id: string;
  window_hours: number;
  sla_percent?: number | null;
  up_seconds?: number | null;
  total_seconds?: number | null;
};

export type GatewayStatusHistory = {
  id: string;
  gateway_id: string;
  status?: string | null;
  reason?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  duration_sec?: number | null;
  created_at?: string | null;
  notes?: string | null;
};

export type GatewayStatusSummary = {
  total: number;
  up: number;
  degraded: number;
  down: number;
  unknown: number;
};

export type GatewayAlertSettings = {
  id?: string | null;
  enabled: boolean;
  notify_down: boolean;
  notify_degraded: boolean;
  notify_recovered: boolean;
  email_recipients: string[];
  telegram_chat_ids: string[];
  quiet_mode?: string | null;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export const gatewaysApi = {
  async list(params?: {
    skip?: number;
    limit?: number;
    residential_complex_id?: string;
    building_id?: string;
    corp_id?: string;
    status?: string;
    monitoring_mode?: string;
    gateway_type?: string;
    map_only?: boolean;
  }) {
    const res = await apiClient.get<Gateway[]>('/gateways/', { params });
    return res.data;
  },
  async get(id: string) {
    const res = await apiClient.get<Gateway>(`/gateways/${id}`);
    return res.data;
  },
  async create(data: Partial<Gateway>) {
    const res = await apiClient.post<Gateway>('/gateways/', data);
    return res.data;
  },
  async update(id: string, data: Partial<Gateway>) {
    const res = await apiClient.put<Gateway>(`/gateways/${id}`, data);
    return res.data;
  },
  async remove(id: string) {
    await apiClient.delete(`/gateways/${id}`);
  },
  async listSla(params?: { window_hours?: number; gateway_ids?: string }) {
    const res = await apiClient.get<GatewaySla[]>('/gateways/sla', { params });
    return res.data;
  },
  async heartbeat(id: string) {
    await apiClient.post(`/gateways/${id}/heartbeat`);
  },
  async check(id: string) {
    const res = await apiClient.post<Gateway>(`/gateways/${id}/check`);
    return res.data;
  },
  async listHistory(
    id: string,
    params?: {
      date_from?: string;
      date_to?: string;
      limit?: number;
      offset?: number;
    }
  ) {
    const res = await apiClient.get<GatewayStatusHistory[]>(`/gateways/${id}/history`, { params });
    return res.data;
  },
  async listLocationHistory(id: string, params?: { limit?: number; offset?: number }) {
    const res = await apiClient.get<GatewayLocationHistory[]>(`/gateways/${id}/location-history`, { params });
    return res.data;
  },
  async getPathHealth(id: string) {
    const res = await apiClient.get<GatewayPathHealth>(`/gateways/${id}/path-health`);
    return res.data;
  },
  async listRemoteLoRaWanGateways(params?: { search?: string; limit?: number }) {
    const res = await apiClient.get<GatewayLoRaWanRemote[]>('/gateways/lorawan/remote', { params });
    return res.data;
  },
  async listLoRaWanBuildingSummary(params?: { residential_complex_id?: string }) {
    const res = await apiClient.get<GatewayLoRaWanBuildingSummary[]>('/gateways/lorawan/buildings/summary', { params });
    return res.data;
  },
  async getLoRaWanMapCoverage(params?: { hours?: number; residential_complex_id?: string }) {
    const res = await apiClient.get<GatewayLoRaWanCoverage>('/gateways/lorawan/map-coverage', { params });
    return res.data;
  },
  async getLoRaWanFloorCoverage(
    buildingId: string,
    params?: { days?: number; system_type?: string },
    signal?: AbortSignal
  ) {
    const res = await apiClient.get<GatewayLoRaWanFloorCoverage>(
      `/gateways/lorawan/buildings/${buildingId}/floor-coverage`,
      { params, signal }
    );
    return res.data;
  },
  async listLoRaWanHealthHistory(id: string, params?: { hours?: number; limit?: number }) {
    const res = await apiClient.get<GatewayLoRaWanHealthPoint[]>(`/gateways/${id}/lorawan/health-history`, { params });
    return res.data;
  },
  async statusSummary() {
    const res = await apiClient.get<GatewayStatusSummary>('/gateways/status-summary');
    return res.data;
  },
  async getAlertSettings() {
    const res = await apiClient.get<GatewayAlertSettings>('/gateways/alert-settings');
    return res.data;
  },
  async updateAlertSettings(payload: GatewayAlertSettings) {
    const res = await apiClient.put<GatewayAlertSettings>('/gateways/alert-settings', payload);
    return res.data;
  },
};
